import React, { useState } from 'react';
import {
  Building,
  Phone,
  Mail,
  User,
  Wrench,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Shield,
  FileText,
  MapPin,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

interface RegistrationFormData {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  servicesDescription: string;
  city: string;
  state: string;
  website: string;
  message: string;
  smsOptIn: boolean;
  termsAccepted: boolean;
}

export default function PublicVendorRegistration() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<RegistrationFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    servicesDescription: '',
    city: '',
    state: '',
    website: '',
    message: '',
    smsOptIn: false,
    termsAccepted: false,
  });

  const handleChange = (field: keyof RegistrationFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (value: string) => {
    handleChange('phone', formatPhoneNumber(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.phone) {
      toast.error('Please fill out all required fields');
      return;
    }

    if (!formData.termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (!formData.smsOptIn) {
      toast.error('SMS consent is required for vendor communications');
      return;
    }

    setIsSubmitting(true);

    try {
      // Import Firebase
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../utils/firebase/client');

      // Save to vendor_registration_requests collection
      await addDoc(collection(db, 'vendor_registration_requests'), {
        ...formData,
        phoneNumber: formData.phone.replace(/\D/g, ''), // Store digits only
        status: 'pending_review',
        source: 'public_website',
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setIsSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Error submitting registration:', error);
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Registration Submitted!
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Thank you for your interest in becoming an OilWellX vendor. We've received your registration request.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-3">What Happens Next?</h3>
            <ol className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="font-bold mr-2">1.</span>
                <span>Our team will review your registration within 1-2 business days</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">2.</span>
                <span>You'll receive an email confirmation at <strong>{formData.email}</strong></span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">3.</span>
                <span>If approved, you'll receive an SMS invitation to complete your full vendor profile</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">4.</span>
                <span>After profile completion, you'll start receiving service requests</span>
              </li>
            </ol>
          </div>
          <div className="text-sm text-gray-600">
            <p>
              <strong>Questions?</strong> Contact us at{' '}
              <a href="mailto:vendors@oilwellx.com" className="text-blue-600 hover:underline">
                vendors@oilwellx.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-blue-600 rounded-2xl flex items-center justify-center">
              <Building className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Become an OilWellX Vendor
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Join our network of trusted service providers for oil & gas field operations
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              icon: CheckCircle,
              title: 'More Jobs',
              description: 'Get connected to operators needing your services',
            },
            {
              icon: Shield,
              title: 'Fast Payments',
              description: 'Automated invoicing and payment processing',
            },
            {
              icon: Wrench,
              title: 'Easy Management',
              description: 'Track tickets, invoices, and communications in one place',
            },
          ].map((benefit, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-md">
              <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <benefit.icon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Vendor Registration</h2>

          {/* Company Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2 text-blue-600" />
              Company Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ABC Services LLC"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Midland"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="TX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Contact Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.contactName}
                  onChange={(e) => handleChange('contactName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Wrench className="h-5 w-5 mr-2 text-blue-600" />
              Services Offered
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe your services <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                value={formData.servicesDescription}
                onChange={(e) => handleChange('servicesDescription', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Well servicing, equipment repair, 24/7 emergency response..."
              />
            </div>
          </div>

          {/* Additional Message */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Additional Information
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tell us more <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Years in business, certifications, coverage area, etc."
              />
            </div>
          </div>

          {/* SMS Opt-In (Required for 800 number compliance) */}
          <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="smsOptIn"
                checked={formData.smsOptIn}
                onChange={(e) => handleChange('smsOptIn', e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <label htmlFor="smsOptIn" className="flex-1 text-sm text-gray-700">
                <strong className="text-blue-900">SMS Communications Consent</strong>
                <p className="mt-1 text-gray-600">
                  By checking this box, you consent to receive SMS text messages from OilWellX at the phone number provided above. 
                  This includes service notifications, job requests, and account updates. Message and data rates may apply. 
                  You can opt out at any time by replying STOP. Message frequency varies.
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  For help, reply HELP or contact{' '}
                  <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:underline">
                    support@oilwellx.com
                  </a>
                  . View our{' '}
                  <a href="/privacy-policy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a href="/terms" className="text-blue-600 hover:underline">
                    Terms of Service
                  </a>
                  .
                </p>
              </label>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="mb-6">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={formData.termsAccepted}
                onChange={(e) => handleChange('termsAccepted', e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <label htmlFor="termsAccepted" className="text-sm text-gray-700">
                I agree to the{' '}
                <a href="/vendor-terms" target="_blank" className="text-blue-600 hover:underline font-medium">
                  Vendor Terms & Conditions
                </a>
                {' '}and{' '}
                <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline font-medium">
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                Submitting...
              </>
            ) : (
              <>
                Submit Registration
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>
            Questions? Email us at{' '}
            <a href="mailto:vendors@oilwellx.com" className="text-blue-600 hover:underline">
              vendors@oilwellx.com
            </a>
          </p>
          <p className="mt-2">
            Already invited?{' '}
            <a href="/vendor/login" className="text-blue-600 hover:underline">
              Complete your profile here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
